import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import type {
  BlockEditorDimmed,
  JSONContent
} from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { useDebouncedCallback } from 'use-debounce'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { usePermissions } from 'lib/rbac'
import {
  hasPresentationChanged,
  mergeEditorPresentationIntoFullPresentation
} from '../utils/presentation'
import { presentationToTiptap } from './adapters/presentationToTiptap'

export const useQuoteBlockEditor = () => {
  const cubeContext = useCubeContext()
  const { can } = usePermissions()
  const { lastLoadedAt } = cubeContext.queries.rawData.editor

  const isEditingAvailable =
    cubeContext.queries.availableFeatures.quote.edit.available.enabled
  const canEditContent = can('org.quote:content.update')
  const isEditable = isEditingAvailable && canEditContent
  const dimmed = match({ isEditingAvailable, canEditContent })
    .returnType<BlockEditorDimmed>()
    .with({ isEditingAvailable: false }, () => 'all')
    .with({ canEditContent: false }, () => 'allExceptCustomBlocks')
    .otherwise(() => 'none')

  /**
   * The editor only reads its content when it's created, which happens for each
   * Cube load, so the presentation is only converted then.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: only rebuilt for a new load
  const initialContent = useMemo(
    () =>
      presentationToTiptap.in(cubeContext.queries.rawData.data.presentation),
    [lastLoadedAt]
  )

  const latestDocRef = useRef<JSONContent | null>(null)

  const syncPresentationToDomain = useDebouncedCallback(() => {
    if (!isEditingAvailable) {
      return
    }

    const currentPresentation = cubeContext.queries.rawData.data.presentation
    const newPresentation = mergeEditorPresentationIntoFullPresentation(
      presentationToTiptap.out({ cubeQueries: cubeContext.queries })(
        latestDocRef.current ?? initialContent
      ),
      currentPresentation
    )

    const currentPresentationAsLoaded =
      mergeEditorPresentationIntoFullPresentation(
        presentationToTiptap.asLoaded(currentPresentation),
        currentPresentation
      )

    if (!hasPresentationChanged(currentPresentationAsLoaded, newPresentation)) {
      return
    }

    cubeContext.mutators.updateData({ presentation: newPresentation })
  }, 500)

  const onChange = useCallback(
    (doc: JSONContent) => {
      latestDocRef.current = doc
      syncPresentationToDomain()
    },
    [syncPresentationToDomain]
  )

  /**
   * Pricing edited outside the editor still needs to reach the saved pricing
   * block, so we also sync when the quote changes. The comparison inside stops
   * this from looping.
   */
  useEffect(() => {
    syncPresentationToDomain()
  }, [cubeContext.queries, syncPresentationToDomain])

  /**
   * A new load is the ground truth, so any queued sync from the previous editor
   * is dropped. It's a layout effect so it runs before the new editor's own
   * effects, which bring its variables up to date, and that change is synced.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs for each new load
  useLayoutEffect(() => {
    syncPresentationToDomain.cancel()
    latestDocRef.current = null
  }, [lastLoadedAt, syncPresentationToDomain])

  return {
    editorKey: lastLoadedAt?.toISOString(),
    initialContent,
    onChange,
    isEditable,
    dimmed
  }
}
